import { buildShellTemplatePreviewHtml } from 'features/EmailEditor/buildShellTemplatePreviewHtml'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import styles from './EmailEditor.module.scss'

const EMAIL_WIDTH = 500
const TOOLTIP_PADDING = 16
const TITLE_HEIGHT = 28
const VIEWPORT_PADDING = 12
const TOOLTIP_MAX_WIDTH = 420

const previewHtmlCache = new Map()
const PREVIEW_CACHE_VERSION = 17

function resolvePreviewHtml(templateKey) {
  if (!templateKey) {
    return ''
  }

  const cacheKey = `${PREVIEW_CACHE_VERSION}:${templateKey}`

  if (!previewHtmlCache.has(cacheKey)) {
    previewHtmlCache.set(cacheKey, buildShellTemplatePreviewHtml(templateKey))
  }

  return previewHtmlCache.get(cacheKey)
}

function computePreviewDimensions(measuredHeight) {
  const maxViewportHeight = Math.min(window.innerHeight * 0.72, 560)
  const chromeHeight = TOOLTIP_PADDING + TITLE_HEIGHT
  const availableHeight = maxViewportHeight - chromeHeight
  const availableWidth = TOOLTIP_MAX_WIDTH - TOOLTIP_PADDING
  const scale = Math.min(availableWidth / EMAIL_WIDTH, availableHeight / measuredHeight, 1)
  const scaledHeight = measuredHeight * scale

  return {
    scale,
    contentHeight: measuredHeight,
    viewportHeight: Math.max(Math.ceil(scaledHeight), 120),
    tooltipWidth: TOOLTIP_MAX_WIDTH,
    tooltipHeight: Math.ceil(scaledHeight + chromeHeight)
  }
}

function resolveTooltipPosition({ anchorRect, tooltipWidth, tooltipHeight }) {
  const offset = 8
  let left = anchorRect.right + offset
  let top = anchorRect.bottom + offset

  if (left + tooltipWidth > window.innerWidth - VIEWPORT_PADDING) {
    left = anchorRect.left - tooltipWidth - offset
  }

  if (top + tooltipHeight > window.innerHeight - VIEWPORT_PADDING) {
    top = anchorRect.top - tooltipHeight - offset
  }

  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING
  }

  if (top < VIEWPORT_PADDING) {
    top = VIEWPORT_PADDING
  }

  return { left, top }
}

const TemplatePreviewTooltip = ({ templateKey, label, anchorRect }) => {
  const contentRef = useRef(null)
  const [dimensions, setDimensions] = useState(() => computePreviewDimensions(480))

  const previewHtml = useMemo(() => resolvePreviewHtml(templateKey), [templateKey])

  useLayoutEffect(() => {
    if (!templateKey || !contentRef.current) {
      return
    }

    const measuredHeight = contentRef.current.scrollHeight
    setDimensions(computePreviewDimensions(measuredHeight))
  }, [previewHtml, templateKey])

  if (!templateKey || !anchorRect) {
    return null
  }

  const { scale, contentHeight, viewportHeight, tooltipWidth, tooltipHeight } = dimensions
  const scaledWidth = EMAIL_WIDTH * scale
  const tooltipPosition = resolveTooltipPosition({
    anchorRect,
    tooltipWidth,
    tooltipHeight
  })

  return createPortal(
    <div
      className={styles.templatePreviewTooltip}
      style={{
        left: tooltipPosition.left,
        top: tooltipPosition.top,
        width: tooltipWidth
      }}
    >
      <div className={styles.templatePreviewTooltipTitle}>{label}</div>
      <div className={styles.templatePreviewTooltipViewport} style={{ height: viewportHeight }}>
        <div
          className={styles.templatePreviewTooltipFrame}
          style={{ width: scaledWidth, height: viewportHeight }}
        >
          <div
            ref={contentRef}
            className={[styles.templatePreviewTooltipScaler, 'no-style-reset'].join(' ')}
            style={{
              width: EMAIL_WIDTH,
              height: contentHeight,
              transform: `scale(${scale})`
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      </div>
    </div>,
    document.body
  )
}

export default TemplatePreviewTooltip
